import { useInfiniteQuery, useQuery } from "react-query";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Badge, Flex, Text, Section } from "@radix-ui/themes";
import { Sample, useSamplingApi } from "@/v2/entities/sampling/Sampling.api";
import { Table } from "@/v2/components/table/Table";
import { useCallback, useMemo, useState } from "react";
import { ExploreInteractionRow } from "@/v2/entities/explore/Explore.model";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import {
  ValidationRow,
  ProgressFormatter,
  TimeFrameFormatter,
  SourcesFormatter,
  enhanceValidationRow,
} from "@/v2/features/validation/validation.utils";
import { Search } from "@/components/shared/inputs/search";
import { useNavigate } from "react-router-dom";
import { buildParams, useExploreApi } from "@/v2/entities/explore/Explore.api";

const columns: ColumnDef<ValidationRow>[] = [
  {
    header: "Sample",
    accessorKey: "name",
    cell: ({ getValue }) => {
      return (
        <Text size="2" weight="medium" highContrast>
          {getValue<string>()}
        </Text>
      );
    },
    size: 250,
  },
  {
    header: "Scorecard",
    accessorKey: "scorecardName",
    size: 250,
  },
  {
    header: "Progress",
    accessorKey: "validated",
    cell: ({ row }) => {
      const { validated } = row.original;
      if (validated === "loading") {
        return (
          <Text size="1" color="gray">
            loading...
          </Text>
        );
      }

      return <ProgressFormatter {...row.original} />;
    },
    meta: {
      className: "w-[100px]",
    },
  },
  {
    header: "Time Frame",
    accessorKey: "dateFrom",
    cell: ({ row }) => {
      return <TimeFrameFormatter {...row.original} />;
    },
    meta: {
      className: "w-[160px]",
    },
  },
  {
    header: "Sources",
    accessorKey: "sources",
    cell: ({ row }) => {
      return <SourcesFormatter {...row.original} />;
    },
    meta: {
      className: "w-[240px]",
    },
  },
  {
    header: "Avg. Score	",
    accessorKey: "avgQaScore",
    cell: ({ getValue, row }) => {
      const avgQaScore = getValue<number | "loading" | undefined>();
      if (avgQaScore == null) {
        return <></>;
      }

      if (avgQaScore === "loading") {
        return (
          <Text size="1" color="gray">
            loading...
          </Text>
        );
      }

      return <AvgQaScoreFormatter value={avgQaScore} />;
    },
    meta: {
      className: "w-[100px]",
    },
  },
  {
    header: "Auto Score",
    accessorKey: "avgAutoQaScore",
    cell: ({ getValue, row }) => {
      const avgAutoQaScore = getValue<number | "loading" | undefined>();
      if (avgAutoQaScore == null) {
        return <></>;
      }

      if (avgAutoQaScore === "loading") {
        return (
          <Text size="1" color="gray">
            loading...
          </Text>
        );
      }

      return <AvgQaScoreFormatter value={avgAutoQaScore} />;
    },
    meta: {
      className: "w-[100px]",
    },
  },
];

const defaultSorting: SortingState = [{ id: "dateFrom", desc: true }];

const LOAD_BATCH_SIZE = 10;

export function ValidationListPage() {
  const navigate = useNavigate();
  const { getSampleConfigurations, getSamples } = useSamplingApi();
  const { getInteractions } = useExploreApi();

  const [searchPhrase, setSearchPhrase] = useState("");
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  const { data: sampleConfigurationNames } = useQuery({
    queryKey: "sampleConfigurations",
    queryFn: () => getSampleConfigurations(),
    select: (data) => {
      return new Map(data.map((x) => [x.id, x.sampleForm?.name ?? "Unnamed"]));
    },
  });

  const { data: samples, isLoading: isLoadingSamples } = useQuery({
    queryKey: ["validations"],
    queryFn: () => getSamples(),
  });

  const nonEmptySamples = useMemo<Sample[]>(() => {
    return (
      samples?.filter(
        (sample) => sample.tickets.length > 0 || sample.conversations.length > 0
      ) ?? []
    );
  }, [samples]);

  const sortedNonEmptySamples = useMemo(() => {
    return [...nonEmptySamples].sort((a, b) => b.id - a.id);
  }, [nonEmptySamples]);

  const samplesInfiniteQuery = useInfiniteQuery({
    queryKey: ["validation-samples-batches", sortedNonEmptySamples],
    queryFn: async ({ pageParam = 0 }) => {
      const startIndex = pageParam as number;
      const batch = sortedNonEmptySamples.slice(
        startIndex,
        startIndex + LOAD_BATCH_SIZE
      );

      const batchInteractions = await Promise.all(
        batch.map(async (sample) => {
          const interactions = [...sample.tickets, ...sample.conversations];
          if (interactions.length === 0) {
            return { sampleId: sample.id, interactions: [] };
          }

          const params = buildParams({ filter: { id: { in: interactions } } });
          const paramsString = params.length > 0 ? `?${params.toString()}` : "";
          const { results } = await getInteractions(paramsString);

          return {
            sampleId: sample.id,
            interactions: results,
          };
        })
      );

      return {
        batch,
        interactions: batchInteractions,
        nextPage: startIndex + LOAD_BATCH_SIZE < sortedNonEmptySamples.length
          ? startIndex + LOAD_BATCH_SIZE
          : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: sortedNonEmptySamples.length > 0,
    refetchOnWindowFocus: false,
  });

  const exploreInteractionsData = useMemo<
    Map<number, ExploreInteractionRow[]>
  >(() => {
    const map = new Map<number, ExploreInteractionRow[]>();
    samplesInfiniteQuery.data?.pages.forEach((page) => {
      page.interactions.forEach(({ sampleId, interactions }) => {
        map.set(sampleId, interactions);
      });
    });
    return map;
  }, [samplesInfiniteQuery.data]);

  const rows = useMemo<ValidationRow[]>(() => {
    return sortedNonEmptySamples.map(
      ({
        id,
        sampleConfigurationId,
        name,
        sampleConfiguration: { qaRules, sources },
        tickets,
        conversations,
        dateFrom,
        dateTo,
      }) => {
        const baseRow: ValidationRow = {
          id,
          name,
          scorecardName:
            sampleConfigurationNames?.get(sampleConfigurationId) ?? "Unnamed",
          validationRules: qaRules,
          total: tickets.length + conversations.length,
          dateFrom,
          dateTo,
          sources,
          validated: "loading",
          avgQaScore: "loading",
          avgAutoQaScore: "loading",
        };

        const interactions = exploreInteractionsData.get(id);
        if (interactions == null) {
          return baseRow;
        }

        return enhanceValidationRow(baseRow, interactions);
      }
    );
  }, [sortedNonEmptySamples, sampleConfigurationNames, exploreInteractionsData]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      row.name.toLowerCase().includes(searchPhrase.toLowerCase())
    );
  }, [rows, searchPhrase]);

  const handleRowClicked = useCallback(
    (row: ValidationRow) => {
      navigate(`/quality/validations/${row.id}`);
    },
    [navigate]
  );

  const handleFetchNextPage = useCallback(async () => {
    await samplesInfiniteQuery.fetchNextPage();
  }, [samplesInfiniteQuery]);

  return (
    <Section size="4" pt="0">
      <Flex p="6" direction="column" gap="3" width="100%" style={{ minHeight: "calc(100dvh - 200px)" }}>
        <Flex direction={"row"} gap={"2"} align="center" justify="between">
          <Flex gap={"2"} align={"center"}>
            <Text size={"3"} weight={"medium"}>
              Validations
            </Text>
            <Badge color="gray">{nonEmptySamples?.length ?? 0}</Badge>
          </Flex>
          <Search
            initialValue={searchPhrase}
            placeholder="Search validations"
            setSearchPhrase={setSearchPhrase}
            debounce={50}
          />
        </Flex>
        <Flex style={{ flex: 1, minHeight: 0 }}>
          <Table
            data={filteredRows}
            columns={columns}
            loading={isLoadingSamples}
            sorting={sorting}
            onSortingChange={setSorting}
            hasNextPage={samplesInfiniteQuery.hasNextPage}
            isFetching={samplesInfiniteQuery.isFetching}
            isFetchingNextPage={samplesInfiniteQuery.isFetchingNextPage}
            fetchNextPage={handleFetchNextPage}
            onRowClicked={handleRowClicked}
          />
        </Flex>
      </Flex>
    </Section>
  );
}
